import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BASE_URL } from '../Config';
import keepaiLogo from '../resource/keepaiLogo.png';
import '../css/style/payment.css';

const INICIS_MODE = process.env.REACT_APP_INICIS_MODE || 'real';
const INICIS_JS_URL = INICIS_MODE === 'test'
    ? 'https://stgstdpay.inicis.com/stdjs/INIStdPay.js'
    : 'https://stdpay.inicis.com/stdjs/INIStdPay.js';
const INICIS_ACTION = INICIS_MODE === 'test'
    ? 'https://stgstdpay.inicis.com/stdPayFront'
    : 'https://stdpay.inicis.com/stdPayFront';
const INICIS_MOBILE_ACTION = INICIS_MODE === 'test'
    ? 'https://stgstdpay.inicis.com/stdPayMob'
    : 'https://stdpay.inicis.com/stdPayMob';

const getAuth = () => {
    const s = localStorage.getItem('user');
    return s ? JSON.parse(s) : null;
};

const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const applySuccess = () => {
    const stored = localStorage.getItem('user');
    if (stored) {
        const u = JSON.parse(stored);
        localStorage.setItem('user', JSON.stringify({ ...u, role: 'Y' }));
    }
};

const Payment = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const user = getAuth();
    const [payData, setPayData] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | ready | paying | success | fail
    const [productType, setProductType] = useState('chatgpt');

    useEffect(() => {
        if (!user?.token) { navigate('/login'); return; }

        // 상품 타입 결정 (URL params 또는 navigate state)
        const params      = new URLSearchParams(window.location.search);
        const stateProduct = location?.state?.product;
        const productParam = params.get('product') || stateProduct;
        const resolvedProductType = productParam === 'claude' ? 'claude' : 'chatgpt';
        setProductType(resolvedProductType);

        // 결제 결과 처리 (데스크탑 postMessage 경유 or 모바일 이니시스 직접 리다이렉트)
        const result  = params.get('result');   // 데스크탑: ?result=success|fail
        const pRspcd  = params.get('P_RSPCD'); // 모바일: 이니시스가 붙여주는 결과코드
        if (result === 'success' || pRspcd === '0000') {
            applySuccess();
            setStatus('success');
            setTimeout(() => navigate('/chat'), 2500);
            return;
        }
        if (result === 'fail' || (pRspcd && pRspcd !== '0000')) {
            setStatus('fail');
            return;
        }

        // 이니시스 JS 로드 (데스크탑 팝업용)
        let script;
        if (!isMobile) {
            script = document.createElement('script');
            script.src = INICIS_JS_URL;
            script.charset = 'UTF-8';
            script.onload  = () => console.log('[PAYMENT] INIStdPay.js 로드 완료:', !!window.INIStdPay);
            script.onerror = () => console.error('[PAYMENT] INIStdPay.js 로드 실패');
            document.head.appendChild(script);
        }

        // 결제 준비 데이터 요청
        console.log('[PAYMENT] prepare 요청 → mode:', INICIS_MODE, '| mobile:', isMobile, '| productType:', resolvedProductType);
        fetch(`${BASE_URL}/payment/prepare`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
            body: JSON.stringify({ productType: resolvedProductType }),
        })
            .then(r => r.json())
            .then(data => {
                console.log('[PAYMENT] prepare 응답:', data);
                setPayData(data);
                setStatus('ready');
            })
            .catch(err => { console.error('[PAYMENT] prepare 오류:', err); setStatus('fail'); });

        // 데스크탑: 팝업에서 결제 결과 수신
        const handleMessage = (e) => {
            if (e.data === 'paySuccess') { applySuccess(); setStatus('success'); setTimeout(() => navigate('/chat'), 2500); }
            else if (e.data === 'payFail') { setStatus('fail'); }
        };
        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
            if (script && document.head.contains(script)) document.head.removeChild(script);
        };
    }, []); // eslint-disable-line

    const buildForm = (formId, mobile) => {
        const existing = document.getElementById(formId);
        if (existing) existing.remove();

        const form = document.createElement('form');
        form.id           = formId;
        form.name         = formId;
        form.method       = 'POST';
        form.action       = mobile ? INICIS_MOBILE_ACTION : INICIS_ACTION;
        form.acceptCharset = 'euc-kr';
        form.style.display = 'none';

        const callbackUrl = `${BASE_URL}/payment/callback?username=${encodeURIComponent(payData.username)}&productType=${payData.productType || 'chatgpt'}`;
        const returnUrl   = `${window.location.origin}/payment`;

        // 데스크탑: P_ 없는 INIStdPay v1.0 형식
        // 모바일: P_ 접두사 INIpay Mobile 형식
        const fields = mobile ? {
            P_INI_PAYMENT : 'CARD',
            P_MID         : payData.mid,
            P_OID         : payData.oid,
            P_AMT         : String(payData.price),
            P_GOODS       : payData.goodname,
            P_UNAME       : user?.name || '',
            P_TIMESTAMP   : String(payData.timestamp),
            P_SIGNATURE   : payData.mobileSignature,
            P_VER         : '2.0',
            P_CHARSET     : 'UTF-8',
            P_NEXT_URL    : callbackUrl,
            P_RETURN_URL  : returnUrl,
        } : {
            version      : '1.0',
            mid          : payData.mid,
            oid          : payData.oid,
            price        : String(payData.price),
            timestamp    : String(payData.timestamp),
            signature    : payData.signature,
            mKey         : payData.mKey,
            currency     : 'WON',
            goodname     : payData.goodname,
            buyername    : user?.name || '',
            buyertel     : '01000000000',
            buyeremail   : '',
            gopaymethod  : 'Card',
            acceptmethod : 'below1000',
            returnUrl    : callbackUrl,
            closeUrl     : returnUrl,
        };

        Object.entries(fields).forEach(([name, value]) => {
            const input = document.createElement('input');
            input.type  = 'hidden';
            input.name  = name;
            input.value = value;
            form.appendChild(input);
        });

        document.body.appendChild(form);
        console.log('[PAYMENT] 폼 생성 완료 — mobile:', mobile, '| mid:', payData.mid, '| oid:', payData.oid);
        return form;
    };

    const handlePay = () => {
        console.log('[PAYMENT] 결제 버튼 클릭 — mobile:', isMobile, '| INIStdPay:', !!window.INIStdPay, '| payData:', payData);
        if (status !== 'ready') return;
        if (!payData?.mid) { console.error('[PAYMENT] payData.mid 없음 — 중단'); return; }

        setStatus('paying');

        if (isMobile) {
            console.log('[PAYMENT] 모바일 — 백엔드 mobile-form으로 리다이렉트');
            const qs = new URLSearchParams({
                mid         : payData.mid,
                oid         : payData.oid,
                price       : String(payData.price),
                goodname    : payData.goodname,
                uname       : user?.name || '',
                timestamp   : String(payData.timestamp),
                sig         : payData.mobileSignature,
                username    : payData.username,
                productType : payData.productType || 'chatgpt',
            }).toString();
            window.location.href = `${BASE_URL}/payment/mobile-form?${qs}`;
        } else {
            if (!window.INIStdPay) { console.error('[PAYMENT] INIStdPay 미로드'); return; }
            console.log('[PAYMENT] 데스크탑 — INIStdPay.pay 호출');
            buildForm('inicisPayFormNative', false);
            setTimeout(() => {
                window.INIStdPay.pay('inicisPayFormNative');
            }, 300);
        }
    };

    if (status === 'success') {
        return (
            <div className="payment-page">
                <div className="payment-card">
                    <div className="payment-result-icon">✅</div>
                    <h2 className="payment-result-title">결제가 완료되었습니다!</h2>
                    <p className="payment-result-desc">잠시 후 채팅 화면으로 이동합니다...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="payment-page">
            <div className="payment-top-bar">
                <img src={keepaiLogo} alt="KeepAI" className="payment-logo" onClick={() => navigate('/')} />
            </div>

            <div className="payment-card">
                <h2 className="payment-card-title">결제하기</h2>

                <div className="payment-product-box">
                    <div className="payment-product-row">
                        <span>상품명</span>
                        <strong>{productType === 'claude' ? 'KeepAI Claude Pro 1개월' : 'KeepAI ChatGPT Plus 1개월'}</strong>
                    </div>
                    <div className="payment-product-row">
                        <span>결제금액</span>
                        <strong className="payment-price">19,000원</strong>
                    </div>
                </div>

                <div className="payment-account-box">
                    <p>※ 계좌이체 문의</p>
                    <p>농협 3010349385051 &nbsp;|&nbsp; 예금주 : 주식회사 리스트</p>
                    <p>연락처 : 1566-2144</p>
                </div>

                {status === 'fail' && (
                    <p className="payment-error">결제에 실패했습니다. 다시 시도해주세요.</p>
                )}

                <button
                    className="payment-btn"
                    onClick={status === 'fail' ? () => { setStatus('loading'); window.location.reload(); } : handlePay}
                    disabled={status === 'loading' || status === 'paying'}
                >
                    {status === 'loading' && '준비 중...'}
                    {status === 'ready'   && '카드 결제하기'}
                    {status === 'paying'  && '결제 진행 중...'}
                    {status === 'fail'    && '다시 시도'}
                </button>
            </div>
        </div>
    );
};

export default Payment;
